import { createSlice, createSelector } from '@reduxjs/toolkit';

/**
 * Emergency Substitution Filters Slice
 *
 * Manages all filter state for the emergency substitution feature.
 * Moving filters to Redux prevents parent component re-renders when filters change.
 */

// Filter categories configuration
const FILTER_CATEGORIES = [
  'allTags',
  'titulacio',
  'disponibilitat',
  'territori',
  'aquiNo',
  'centresTreballats',
];

// Initial state factory
const createFilterState = () => {
  const state = {
    searchText: '',
    // Bolsa (organization) filter — server-side, separate from client-side filters.
    // Stores selected organization names (e.g. ['FASI'], ['RESILIS'], ['FASI','RESILIS']).
    // Pre-initialized with the request's organization when the detail page loads.
    pendingBolsaFilter: [],
    appliedBolsaFilter: [],
  };
  FILTER_CATEGORIES.forEach(category => {
    state[`applied${category.charAt(0).toUpperCase() + category.slice(1)}Filters`] = [];
    state[`pending${category.charAt(0).toUpperCase() + category.slice(1)}Filters`] = [];
  });
  return state;
};

const initialState = createFilterState();

const emergencyFiltersSlice = createSlice({
  name: 'emergencyFilters',
  initialState,
  reducers: {
    setSearchText: (state, action) => {
      state.searchText = action.payload;
    },

    // Bolsa (pool) filter — server-side, changes the API query
    setPendingBolsaFilter: (state, action) => {
      state.pendingBolsaFilter = action.payload;
    },

    // Generate pending filter setters dynamically
    ...FILTER_CATEGORIES.reduce((reducers, category) => {
      const capitalizedCategory = category.charAt(0).toUpperCase() + category.slice(1);
      reducers[`setPending${capitalizedCategory}Filters`] = (state, action) => {
        state[`pending${capitalizedCategory}Filters`] = action.payload;
      };
      return reducers;
    }, {}),

    // Apply pending filters to applied filters
    applyPendingFilters: (state) => {
      state.appliedBolsaFilter = state.pendingBolsaFilter;
      FILTER_CATEGORIES.forEach(category => {
        const capitalizedCategory = category.charAt(0).toUpperCase() + category.slice(1);
        state[`applied${capitalizedCategory}Filters`] = state[`pending${capitalizedCategory}Filters`];
      });
    },

    // Initialize pending with applied (when opening offcanvas)
    initializePendingFilters: (state) => {
      state.pendingBolsaFilter = state.appliedBolsaFilter;
      FILTER_CATEGORIES.forEach(category => {
        const capitalizedCategory = category.charAt(0).toUpperCase() + category.slice(1);
        state[`pending${capitalizedCategory}Filters`] = state[`applied${capitalizedCategory}Filters`];
      });
    },

    // Reset bolsa filter to default (use organization from request)
    resetBolsaFilter: (state) => {
      state.pendingBolsaFilter = [];
      state.appliedBolsaFilter = [];
    },

    clearAllFilters: () => initialState,

    setFiltersFromUrl: (state, action) => {
      const filters = action.payload;

      if (filters.searchText !== undefined) {
        state.searchText = filters.searchText;
      }

      FILTER_CATEGORIES.forEach(category => {
        const capitalizedCategory = category.charAt(0).toUpperCase() + category.slice(1);
        const filterKey = `${category}Filters`;
        if (filters[filterKey]) {
          state[`applied${capitalizedCategory}Filters`] = filters[filterKey];
          state[`pending${capitalizedCategory}Filters`] = filters[filterKey];
        }
      });
    },
  },
});

// Export actions
export const {
  setSearchText,
  setPendingBolsaFilter,
  resetBolsaFilter,
  setPendingAllTagsFilters,
  setPendingTitulacioFilters,
  setPendingDisponibilitatFilters,
  setPendingTerritoriFilters,
  setPendingAquiNoFilters,
  setPendingCentresTreballatsFilters,
  applyPendingFilters,
  initializePendingFilters,
  clearAllFilters,
  setFiltersFromUrl,
} = emergencyFiltersSlice.actions;

// Base selectors
export const selectSearchText = (state) => state.emergencyFilters.searchText;
export const selectPendingBolsaFilter = (state) => state.emergencyFilters.pendingBolsaFilter;
export const selectAppliedBolsaFilter = (state) => state.emergencyFilters.appliedBolsaFilter;

// Generate filter selectors dynamically
const createFilterSelectors = (prefix) => {
  const selectors = {};
  FILTER_CATEGORIES.forEach(category => {
    const capitalizedCategory = category.charAt(0).toUpperCase() + category.slice(1);
    const selectorName = `select${prefix}${capitalizedCategory}Filters`;
    selectors[selectorName] = (state) =>
      state.emergencyFilters[`${prefix.toLowerCase()}${capitalizedCategory}Filters`];
  });
  return selectors;
};

// Applied filter selectors
export const {
  selectAppliedAllTagsFilters,
  selectAppliedTitulacioFilters,
  selectAppliedDisponibilitatFilters,
  selectAppliedTerritoriFilters,
  selectAppliedAquiNoFilters,
  selectAppliedCentresTreballatsFilters,
} = createFilterSelectors('Applied');

// Pending filter selectors
export const {
  selectPendingAllTagsFilters,
  selectPendingTitulacioFilters,
  selectPendingDisponibilitatFilters,
  selectPendingTerritoriFilters,
  selectPendingAquiNoFilters,
  selectPendingCentresTreballatsFilters,
} = createFilterSelectors('Pending');

// Composite selectors
export const selectAllAppliedFilters = createSelector(
  [
    selectSearchText,
    ...Object.values(createFilterSelectors('Applied')),
  ],
  (searchText, ...filters) => ({
    searchText,
    allTagsFilters: filters[0],
    titulacioFilters: filters[1],
    disponibilitatFilters: filters[2],
    territoriFilters: filters[3],
    aquiNoFilters: filters[4],
    centresTreballatsFilters: filters[5],
  })
);

export const selectAllPendingFilters = createSelector(
  [
    selectSearchText,
    ...Object.values(createFilterSelectors('Pending')),
  ],
  (searchText, ...filters) => ({
    searchText,
    allTagsFilters: filters[0],
    titulacioFilters: filters[1],
    disponibilitatFilters: filters[2],
    territoriFilters: filters[3],
    aquiNoFilters: filters[4],
    centresTreballatsFilters: filters[5],
  })
);

// Count selectors
const createCountSelector = (prefix) => {
  const selectors = Object.values(createFilterSelectors(prefix));
  return createSelector(
    [selectSearchText, ...selectors],
    (searchText, ...filters) => {
      let count = 0;
      if (searchText) count++;
      filters.forEach(filter => {
        if (filter.length > 0) count += filter.length;
      });
      return count;
    }
  );
};

export const selectPendingFilterCount = createCountSelector('Pending');
export const selectActiveFilterCount = createCountSelector('Applied');

export default emergencyFiltersSlice.reducer;
